<template>
  <div class="reserve-params-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('reserveParamsManage.list.title')" :subtitle="$t('reserveParamsManage.page.subtitle')" />

    <!-- (2) SearchPanel -->
    <ds-search-panel>
      <el-row :gutter="16">
        <el-col :xs="24" :sm="12" :md="8" :lg="6">
          <el-input v-model.trim="searchForm.name" clearable
            :placeholder="$t('reserveParamsManage.search.namePlaceholder')" @keyup.enter.native="handleSearch" />
        </el-col>
      </el-row>

      <template #actions>
        <el-button type="primary" icon="el-icon-search" @click="handleSearch">
          {{ $t('common.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" @click="handleReset">
          {{ $t('common.reset') }}
        </el-button>
      </template>
    </ds-search-panel>

    <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
    <div class="ds-panel">
      <ds-toolbar :total="page.total" :density="density" :show-column-setting="false" :show-refresh="true"
        @update:density="density = $event" @refresh="getList">
        <template #left>
          <span class="ds-panel__title">{{ $t('reserveParamsManage.list.title') }}</span>
        </template>
        <template #right>
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleAdd">
            {{ $t('common.add') }}
          </el-button>
        </template>
      </ds-toolbar>

      <ds-data-table :data="tableData" :loading="loading" :error="hasError" :error-message="errorMessage"
        :density="density" border :empty-description="$t('reserveParamsManage.page.empty')" @retry="getList">
        <el-table-column prop="name" :label="$t('reserveParamsManage.table.name')" align="left" min-width="160" show-overflow-tooltip />
        <el-table-column prop="paramWay" :label="$t('reserveParamsManage.table.paramWay')" align="center" width="120">
          <template slot-scope="scope">
            <ds-status-tag :label="scope.row.paramWay === '1' ? $t('reserveParamsManage.day') : $t('reserveParamsManage.week')"
              :text="scope.row.paramWay === '1' ? $t('reserveParamsManage.day') : $t('reserveParamsManage.week')" type="info" />
          </template>
        </el-table-column>
        <el-table-column prop="startTime" :label="$t('reserveParamsManage.table.startTime')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.startTime || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="maxQuantity" :label="$t('reserveParamsManage.table.maxQuantity')" align="center" width="130">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.maxQuantity }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="hoursMaxQuantity" :label="$t('reserveParamsManage.table.hoursMaxQuantity')" align="center" width="150">
          <template slot-scope="scope">
            <span class="ds-num">{{ scope.row.hoursMaxQuantity }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('common.operation')" align="center" width="240" fixed="right">
          <template slot-scope="scope">
            <el-button size="mini" type="text" @click="handleSetTime(scope.row)">
              {{ $t('reserveParamsManage.setTime') }}
            </el-button>
            <el-button size="mini" type="text" @click="handleEdit(scope.row)">
              {{ $t('common.edit') }}
            </el-button>
            <el-button size="mini" type="text" class="ds-btn-danger" @click="handleDelete(scope.row)">
              {{ $t('common.delete') }}
            </el-button>
          </template>
        </el-table-column>
      </ds-data-table>

      <ds-pagination-bar :total="page.total" :page="page.current" :size="page.size" :page-sizes="[10, 20, 30, 50]"
        @size-change="handleSizeChange" @current-change="handleCurrentChange" />
    </div>

    <!-- 子组件 -->
    <add-reserve-params ref="addReserveParams" @success="handleSuccess" />
    <edit-reserve-params ref="editReserveParams" @success="handleSuccess" />
    <delete-reserve-params ref="deleteReserveParams" @success="handleSuccess" />
    <edit-reserve-params-open-time ref="editReserveParamsOpenTime" @success="handleSuccess" />
  </div>
</template>

<script>
import { listReserveParams } from '@/api/scm/reserveParamsManageApi'
import { getCommunityId } from '@/api/community/communityApi'
import AddReserveParams from '@/components/scm/addReserveParams'
import EditReserveParams from '@/components/scm/editReserveParams'
import DeleteReserveParams from '@/components/scm/deleteReserveParams'
import EditReserveParamsOpenTime from '@/components/scm/editReserveParamsOpenTime'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'
import DsStatusTag from '@/components/ds/ds-status-tag.vue'

export default {
  name: 'ReserveParamsManageList',
  components: {
    AddReserveParams,
    EditReserveParams,
    DeleteReserveParams,
    EditReserveParamsOpenTime,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar,
    DsStatusTag
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      searchForm: {
        name: ''
      },
      tableData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      communityId: ''
    }
  },
  created() {
    this.communityId = getCommunityId()
    this.getList()
  },
  methods: {
    async getList() {
      try {
        this.loading = true
        this.hasError = false
        this.errorMessage = ''
        const params = {
          page: this.page.current,
          row: this.page.size,
          name: this.searchForm.name,
          communityId: this.communityId
        }
        const { data, total } = await listReserveParams(params)
        this.tableData = Array.isArray(data) ? data : []
        this.page.total = total || 0
      } catch (error) {
        console.error('查询预约参数失败:', error)
        this.hasError = true
        this.errorMessage = error.message || this.$t('reserveParamsManage.page.loadFailed')
      } finally {
        this.loading = false
      }
    },
    handleSearch() {
      this.page.current = 1
      this.getList()
    },
    handleReset() {
      this.searchForm.name = ''
      this.page.current = 1
      this.getList()
    },
    handleAdd() {
      this.$refs.addReserveParams.open()
    },
    handleEdit(row) {
      this.$refs.editReserveParams.open(row)
    },
    handleSetTime(row) {
      this.$refs.editReserveParamsOpenTime.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteReserveParams.open(row)
    },
    handleSuccess() {
      this.getList()
    },
    handleSizeChange(val) {
      this.page.size = val
      this.page.current = 1
      this.getList()
    },
    handleCurrentChange(val) {
      this.page.current = val
      this.getList()
    }
  }
}
</script>

<style lang="scss" scoped>
.reserve-params-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}

.ds-btn-danger {
  color: var(--ds-danger) !important;
}
</style>
